// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type DialogProps } from '@mui/material/Dialog';
import { createDataProvider } from '@shared/ui-kit/providers';
import produce from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { useClientData } from './client-provider';
import { useTranslation } from 'react-i18next';
import type { CreateAdvisoryProposalsRequestBody } from '@shared/data-access/types/brokerage-types';

/** Account summary for display and API (iban required for API). */
export interface SelectedAccount {
  iban: string;
  name: string;
}

/** Institution with its selected accounts for the advisory proposal. */
export interface SelectedInstitution {
  id: string;
  name: string;
  accounts: SelectedAccount[];
}

interface AdvisoryProposalData {
  advisory_type: string;
  advisory_type_label: string;
  commission_managed_assets: number;
  commission_id: string;
  commission_label: string;
  retrocession: string;
  retrocession_label: string;
  /** Institutions and their accounts to include in the advisory proposal. */
  selectedInstitutions: SelectedInstitution[];
}

export const useProfilingFactory = ({ hidden }: { hidden?: boolean }) => {
  const { t } = useTranslation();

  const DEFAULT_OPTIONS: AdvisoryProposalData = useMemo(
    () => ({
      advisory_type: 'non-independent',
      advisory_type_label: t(
        'activation-step2.advisory-types.non-independent.label',
        {
          ns: 'components',
        },
      ) as string,
      commission_managed_assets: 0,
      commission_id: 'no-commission',
      commission_label: t(
        'activation-step4.commission-options.no-commission.name',
        {
          ns: 'components',
        },
      ),
      retrocession: 'no',
      retrocession_label: 'No',
      selectedInstitutions: [],
    }),
    [t],
  );

  const { onCreateAdvisoryProposal, createAdvisoryProposalResult } =
    useClientData();

  const [open, setOpen] = useState(false);

  const [activeStep, setActiveStep] = useState(0);

  const [steps, setSteps] = useState([
    {
      id: 'advisory-type' as const,
      label: t('activation-steps.activation-step2', { ns: 'components' }) as string,
      valid: false,
      canBack: false,
    },
    {
      id: 'accounts-to-advise' as const,
      label: t('activation-steps.activation-step1', { ns: 'components' }) as string,
      valid: false,
      canBack: true,
    },
    {
      id: 'commission' as const,
      label: t('activation-steps.activation-step4', { ns: 'components' }) as string,
      valid: false,
      canBack: true,
    },
    {
      id: 'review' as const,
      label: t('activation-steps.activation-step5.label', { ns: 'components' }),
      valid: true,
      canBack: true,
      nextLabel: t('activation-steps.activation-step5.next-label', {
        ns: 'components',
      }) as string,
    },
    {
      id: 'client-handoff' as const,
      label: t('activation-steps.activation-step6', { ns: 'components' }),
      valid: true,
      canBack: false,
    },
  ]);

  const step = useMemo(() => steps[activeStep], [steps, activeStep]);

  const [data, setData] = useState(DEFAULT_OPTIONS);

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onCloseDialog: Required<DialogProps>['onClose'] = useCallback(
    (_, reason) => {
      if (reason && reason === 'backdropClick') return;

      onClose();
    },
    [onClose],
  );

  const onOpen = useCallback(() => {
    setActiveStep(0);

    setData(DEFAULT_OPTIONS);

    setOpen(true);
  }, [DEFAULT_OPTIONS]);

  const onNext = useCallback(() => {
    if (!step?.valid) return;

    if (step?.id === 'advisory-type' && data.advisory_type === 'discretionary') {
      setActiveStep(3);

      return;
    }

    if (step?.id === 'review') {
      const ibans = data.selectedInstitutions.flatMap(inst =>
        inst.accounts.map(a => a.iban),
      );
      const payload: Omit<CreateAdvisoryProposalsRequestBody, 'client_id'> = {
        advisory_type: data.advisory_type,
        commission_managed_assets: data.commission_managed_assets,
        ibans,
      };
      onCreateAdvisoryProposal(payload, () => {
        setActiveStep(prevActiveStep => prevActiveStep + 1);
      });

      return;
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
  }, [
    data.advisory_type,
    data.commission_managed_assets,
    data.selectedInstitutions,
    onCreateAdvisoryProposal,
    step,
  ]);

  const onPrevious = useCallback(() => {
    setActiveStep(prevActiveStep =>
      prevActiveStep === 3 && data.advisory_type === 'discretionary'
        ? 0
        : prevActiveStep - 1,
    );
  }, [data.advisory_type]);

  const onSave = useCallback(() => {
    onClose();
  }, [onClose]);

  const updateStepValidity = useCallback(
    (id: (typeof steps)[number]['id'], valid: boolean) => {
      setSteps(
        produce(draft => {
          const step = draft.find(step => step.id === id);
          if (step) {
            step.valid = valid;
          }
        }),
      );
    },
    [],
  );

  const updateAdvisoryData = useCallback((payload: Partial<typeof data>) => {
    setData(
      produce(draft => {
        Object.assign(draft, payload);
      }),
    );
  }, []);

  return {
    open,
    step,
    steps,
    activeStep,
    data,
    updateStepValidity,
    updateAdvisoryData,
    onClose,
    onCloseDialog,
    onOpen,
    onNext,
    onPrevious,
    onSave,
    isLoading: createAdvisoryProposalResult.isLoading,
    hidden,
  } as const;
};

export const { useProfilingData, ProfilingProvider, withProfiling } =
  createDataProvider({
    factory: useProfilingFactory,
    name: 'Profiling',
  });
